/**
 * EditableTextArea component that manages its own state to prevent cursor jumping
 */
import React, { useState, useRef, useEffect } from "react"
import { Input } from "antd"

const { TextArea } = Input

interface EditableTextAreaProps {
  value: string
  onChange: (value: string) => void
  id: string
}

export const EditableTextArea: React.FC<EditableTextAreaProps> = ({
  value,
  onChange,
}) => {
  const [localValue, setLocalValue] = useState(value)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isTypingRef = useRef(false)

  // Update local value when prop changes (but not during typing)
  useEffect(() => {
    if (!isTypingRef.current) {
      setLocalValue(value)
    }
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    setLocalValue(newValue)
    isTypingRef.current = true

    // Debounce the onChange callback to avoid excessive re-renders
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      onChange(newValue)
      isTypingRef.current = false
    }, 300)
  }

  // Flush changes on blur to ensure they're saved
  const handleBlur = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    onChange(localValue)
    isTypingRef.current = false
  }

  // Clean up timeout on unmount and save final value
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      // Save the final value on unmount
      if (isTypingRef.current) {
        onChange(localValue)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localValue])

  return (
    <TextArea
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      autoSize={{ minRows: 2 }}
      spellCheck={false}
      className="border-none font-mono text-xs w-full"
      style={{ width: "100%", resize: "none" }}
    />
  )
}
